import { createRoute, Outlet, Navigate } from '@tanstack/react-router'
import { useAuth } from '~/auth'
import { useIsAdmin } from '~/auth/useRoles'
import styles from './access.module.css'

// Import admin page components
import { AdminDashboardPage } from './dashboard'
import { AdminOrdersPage } from './orders'
import { AdminOrderDetailPage } from './order-detail'
import { InvoicePage } from './invoice'
import { AdminDriversPage } from './drivers'
import { AdminUsersPage } from './users'
import { AdminSettingsPage } from './settings'
import { AdminDeliveryZonesPage } from './delivery-zones'

// Root route placeholder - will be replaced when wired into main route tree
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let parentRoute: any = null

export function setAdminParentRoute(route: unknown) {
  parentRoute = route
}

function AdminAccessState({
  code,
  title,
  body,
}: {
  code: string
  title: string
  body: string
}) {
  return (
    <div className={styles.accessPage}>
      <div className={styles.accessPanel}>
        <img src="/brand/logomark.png" alt="" className={styles.accessLogo} />
        <span className={styles.accessCode}>{code}</span>
        <h1 className={styles.accessTitle}>{title}</h1>
        <p className={styles.accessBody}>{body}</p>
        <a className={styles.accessAction} href="/">
          BACK TO STORE
        </a>
      </div>
    </div>
  )
}

/**
 * Admin Route Guard Component
 * Checks if the user is authenticated and has admin role.
 * Redirects to home page if not authorized.
 */
function AdminRouteGuard() {
  const { isAuthenticated, isLoading } = useAuth()
  const isAdmin = useIsAdmin()

  // Show loading state while checking auth
  if (isLoading) {
    return <AdminAccessState code="..." title="Checking Access" body="Verifying your Tasamago operations role." />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" search={{ reset: undefined }} />
  }

  if (!isAdmin) {
    return (
      <AdminAccessState
        code="403"
        title="Access Denied"
        body="This area is for Tasamago operations staff. Your account does not have the admin role."
      />
    )
  }

  return <Outlet />
}

/**
 * Create admin routes given a parent route.
 * This function should be called from the main route file to wire up admin routes.
 */
export function createAdminRoutes(rootRoute: unknown) {
  setAdminParentRoute(rootRoute)

  // Admin layout route - wraps all admin pages with auth guard
  const adminRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: '/admin',
    component: AdminRouteGuard,
  })

  // Dashboard route (index for /admin)
  const adminDashboardRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/',
    component: AdminDashboardPage,
  })

  // Orders list route
  const adminOrdersRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/orders',
    component: AdminOrdersPage,
  })

  // Order detail route with dynamic parameter
  const adminOrderDetailRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/orders/$orderId',
    component: AdminOrderDetailPage,
  })

  // Invoice route - printable invoice for an order
  const adminInvoiceRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/orders/$orderId/invoice',
    component: InvoicePage,
  })

  // Drivers route
  const adminDriversRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/drivers',
    component: AdminDriversPage,
  })

  // Users route
  const adminUsersRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/users',
    component: AdminUsersPage,
  })

  // Settings route
  const adminSettingsRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/settings',
    component: AdminSettingsPage,
  })

  // Delivery zones route
  const adminDeliveryZonesRoute = createRoute({
    getParentRoute: () => adminRoute,
    path: '/delivery-zones',
    component: AdminDeliveryZonesPage,
  })

  // Add all child routes to admin route
  return adminRoute.addChildren([
    adminDashboardRoute,
    adminOrdersRoute,
    adminOrderDetailRoute,
    adminInvoiceRoute,
    adminDriversRoute,
    adminUsersRoute,
    adminSettingsRoute,
    adminDeliveryZonesRoute,
  ])
}
